import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Lock, RefreshCw, LogOut, Phone, CalendarDays, Clock, StickyNote, Inbox, Megaphone, Save, Printer, Star } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "elite_admin_token";

const STATUSES = [
  { key: "new", label: "New", cls: "border-[#c5a059] text-[#c5a059] bg-[#c5a059]/10" },
  { key: "called", label: "Called", cls: "border-sky-400/60 text-sky-300 bg-sky-400/10" },
  { key: "confirmed", label: "Confirmed", cls: "border-emerald-400/60 text-emerald-300 bg-emerald-400/10" },
  { key: "done", label: "Done", cls: "border-white/25 text-[#8a9690] bg-white/5" },
];

const REVIEW_URL = "https://www.google.com/maps/search/?api=1&query=Elite+Spa+1399+Kipling+St+Lakewood+CO+80215";

const reviewSms = (b) =>
  `sms:${b.phone.replace(/[^+\d]/g, "")}?&body=${encodeURIComponent(
    `Hi ${b.name}, thank you for visiting Elite SPA! If you enjoyed your session, we'd be so grateful for a quick Google review: ${REVIEW_URL}`
  )}`;

export default function AdminInbox() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState({ title: "", message: "", enabled: false });
  const [savingPromo, setSavingPromo] = useState(false);

  const fetchBookings = useCallback(async (tk) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${tk}` } });
      setBookings(data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Could not load bookings.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchBookings(token);
      axios.get(`${API}/promo`).then(({ data }) => setPromo(data)).catch(() => {});
    }
  }, [token, fetchBookings]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const savePromo = async () => {
    setSavingPromo(true);
    try {
      await axios.put(`${API}/promo`, promo, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Promo spotlight saved.");
    } catch {
      toast.error("Could not save promo.");
    } finally {
      setSavingPromo(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPasscode("");
    } catch {
      setError("Incorrect passcode. Please try again.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setBookings([]);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050706] flex items-center justify-center px-6">
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md border border-white/10 bg-[#0c100e] p-10"
        >
          <div className="flex items-center gap-3 text-[#c5a059]">
            <Lock size={18} />
            <span className="text-[11px] tracking-[0.4em] uppercase">Private Area</span>
          </div>
          <h1 className="mt-4 font-serif-display text-3xl text-[#f0f4f2]">Booking Inbox</h1>
          <p className="mt-2 text-sm text-[#8a9690] font-light">Enter the spa passcode to view booking requests.</p>
          <Input
            data-testid="admin-passcode-input"
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Passcode"
            className="mt-8 h-12 rounded-none bg-[#050706] border-white/15 text-[#f0f4f2] placeholder:text-[#8a9690]/60 focus-visible:ring-[#c5a059] focus-visible:ring-1 focus-visible:ring-offset-0"
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-sm text-red-400/90">{error}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-6 w-full bg-[#c5a059] hover:bg-[#dcc286] text-[#050706] py-3.5 text-xs tracking-[0.25em] uppercase font-medium transition-colors duration-300"
          >
            Unlock Inbox
          </button>
          <a href="/" className="mt-6 block text-center text-xs text-[#8a9690] hover:text-[#c5a059] transition-colors duration-300">
            ← Back to website
          </a>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050706] py-14 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 text-[#c5a059]">
              <Inbox size={18} />
              <span className="text-[11px] tracking-[0.4em] uppercase">Elite SPA · Private</span>
            </div>
            <h1 className="mt-3 font-serif-display text-4xl text-[#f0f4f2]">Booking Requests</h1>
            <p className="mt-2 text-sm text-[#8a9690] font-light">
              {bookings.length} request{bookings.length === 1 ? "" : "s"} · newest first
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/menu"
              data-testid="admin-print-menu-link"
              className="flex items-center gap-2 border border-[#c5a059]/40 text-[#c5a059] hover:bg-[#c5a059] hover:text-[#050706] px-5 py-2.5 text-xs tracking-[0.15em] uppercase transition-colors duration-300"
            >
              <Printer size={13} /> Print Menu
            </a>
            <button
              onClick={() => fetchBookings(token)}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 border border-white/15 text-[#8a9690] hover:border-[#c5a059] hover:text-[#c5a059] px-5 py-2.5 text-xs tracking-[0.15em] uppercase transition-colors duration-300"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-white/15 text-[#8a9690] hover:border-red-400/60 hover:text-red-400 px-5 py-2.5 text-xs tracking-[0.15em] uppercase transition-colors duration-300"
            >
              <LogOut size={13} /> Lock
            </button>
          </div>
        </div>

        <div className="mt-10 border border-[#c5a059]/25 bg-[#0c100e] p-6 lg:p-8" data-testid="promo-editor">
          <div className="flex items-center gap-3 text-[#c5a059]">
            <Megaphone size={16} />
            <span className="text-[11px] tracking-[0.35em] uppercase">Seasonal Promo Spotlight</span>
          </div>
          <p className="mt-2 text-xs text-[#8a9690] font-light">
            Shown as a gold banner on the website when enabled. Update it anytime — no redesign needed.
          </p>
          <div className="mt-5 grid lg:grid-cols-12 gap-4">
            <Input
              data-testid="promo-title-input"
              value={promo.title}
              onChange={(e) => setPromo((p) => ({ ...p, title: e.target.value }))}
              placeholder="Title (e.g. Mother's Day Special)"
              className="lg:col-span-4 h-11 rounded-none bg-[#050706] border-white/15 text-[#f0f4f2] placeholder:text-[#8a9690]/60 focus-visible:ring-[#c5a059] focus-visible:ring-1 focus-visible:ring-offset-0"
            />
            <Textarea
              data-testid="promo-message-input"
              value={promo.message}
              onChange={(e) => setPromo((p) => ({ ...p, message: e.target.value }))}
              placeholder="Message (e.g. Bring your mom — two foot massages for the price of one, all May.)"
              rows={1}
              className="lg:col-span-8 min-h-[44px] rounded-none bg-[#050706] border-white/15 text-[#f0f4f2] placeholder:text-[#8a9690]/60 focus-visible:ring-[#c5a059] focus-visible:ring-1 focus-visible:ring-offset-0 resize-none"
            />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button
              data-testid="promo-enabled-toggle"
              onClick={() => setPromo((p) => ({ ...p, enabled: !p.enabled }))}
              className={`flex items-center gap-2 border px-4 py-2 text-[11px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                promo.enabled ? "border-emerald-400/60 text-emerald-300 bg-emerald-400/10" : "border-white/20 text-[#8a9690]"
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${promo.enabled ? "bg-emerald-400" : "bg-[#8a9690]/50"}`} />
              {promo.enabled ? "Live on site" : "Hidden"}
            </button>
            <button
              data-testid="promo-save-button"
              onClick={savePromo}
              disabled={savingPromo}
              className="flex items-center gap-2 bg-[#c5a059] hover:bg-[#dcc286] text-[#050706] px-5 py-2 text-[11px] tracking-[0.2em] uppercase font-medium disabled:opacity-60 transition-colors duration-300"
            >
              <Save size={13} /> {savingPromo ? "Saving…" : "Save Promo"}
            </button>
          </div>
        </div>

        <div className="mt-10 space-y-4">
          {bookings.length === 0 && !loading && (
            <div data-testid="admin-empty-state" className="border border-white/10 p-14 text-center">
              <p className="font-serif-display text-2xl text-[#8a9690]">No booking requests yet.</p>
              <p className="mt-2 text-sm text-[#8a9690]/70 font-light">New requests from the website will appear here.</p>
            </div>
          )}
          {bookings.map((b, i) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.5) }}
              data-testid={`admin-booking-${b.id}`}
              className="border border-white/10 bg-[#0c100e] p-6 lg:p-8 hover:border-[#c5a059]/30 transition-colors duration-300"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-serif-display text-2xl text-[#f0f4f2]">{b.name}</h2>
                  <span className="mt-1 inline-block text-[10px] tracking-[0.25em] uppercase text-[#c5a059] border border-[#c5a059]/30 px-3 py-1">
                    {b.service}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <span className="text-xs text-[#8a9690]/70 font-light">
                    received {new Date(b.created_at).toLocaleString()}
                  </span>
                  <div className="flex gap-1.5" data-testid={`status-buttons-${b.id}`}>
                    {STATUSES.map((s) => (
                      <button
                        key={s.key}
                        data-testid={`status-${s.key}-${b.id}`}
                        onClick={() => setStatus(b.id, s.key)}
                        className={`px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase border transition-colors duration-300 ${
                          (b.status || "new") === s.key ? s.cls : "border-white/10 text-[#8a9690]/50 hover:text-[#8a9690]"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-5 grid sm:grid-cols-3 gap-4 text-sm text-[#8a9690] font-light">
                <a href={`tel:${b.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2 hover:text-[#c5a059] transition-colors duration-300">
                  <Phone size={14} className="text-[#c5a059]" /> {b.phone}
                </a>
                <span className="flex items-center gap-2">
                  <CalendarDays size={14} className="text-[#c5a059]" /> {b.preferred_date}
                </span>
                <span className="flex items-center gap-2">
                  <Clock size={14} className="text-[#c5a059]" /> {b.preferred_time}
                </span>
              </div>
              {b.notes && (
                <p className="mt-4 flex items-start gap-2 text-sm text-[#f0f4f2]/70 font-light leading-relaxed border-t border-white/5 pt-4">
                  <StickyNote size={14} className="text-[#c5a059] mt-0.5 shrink-0" /> {b.notes}
                </p>
              )}
              {b.status === "done" && (
                <a
                  href={reviewSms(b)}
                  data-testid={`review-invite-sms-${b.id}`}
                  className="mt-4 inline-flex items-center gap-2 border border-[#c5a059]/40 text-[#c5a059] hover:bg-[#c5a059] hover:text-[#050706] px-4 py-2 text-[10px] tracking-[0.2em] uppercase transition-colors duration-300"
                >
                  <Star size={12} /> Text Review Invite
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
